'use client';

import { c as _c } from "react/compiler-runtime";
import type { UseChatHelpers } from '@ai-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { useEventCallback } from 'usehooks-ts';
import type { AiContext } from './types/ai-context';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { prepareHydratedCodeContentForChatRequest } from './code-content-for-chat-request';
import { buildSidebarChatAiContext } from './pr-v2-visible-file';
import { applyAddPrComment, hasRequiredAddPrCommentInput } from './tools/apply-add-pr-comment';
import { usePrCommentInput } from '@/components/inbox/prs/hooks/use-pr-comment-input';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { useChatStore } from '@/lib/stores/chat-store';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { usePrStore } from '@/lib/stores/pr-store';
import { usePrTabNavigationStore } from '@/lib/stores/pr-tab-navigation/tab-navigation-store';
import { usePrV2DraftStore } from '@/lib/stores/pr-v2-draft-store';

/**
 * What the sidebar's chat needs of the engine that answers it, whichever it is: the AI SDK's
 * `useChat` (`chat.tsx`) or a pull request page's code session (`code-session/`).
 */

/** The helpers of `useChat` the sidebar's chat uses, which any engine it runs on gives it. */
export type SidebarChatEngine = Pick<UseChatHelpers<SidebarChatMessage>, 'messages' | 'setMessages' | 'sendMessage' | 'status' | 'stop' | 'error' | 'clearError'>;

/** How a turn of the chat ended, for what the chat shows and counts after it. */
export type SidebarChatTurnEnd = {
  lastMessageId: string | null;
  /** The answer completed, rather than being stopped, cut off or failing. */
  completed: boolean;
};
export type SidebarChatToolCallEvent = {
  toolCall: {
    toolCallId: string;
    toolName: string;
    input: unknown;
    /** Sessions supply message-scoped identity; legacy AI SDK callbacks omit it. */
    replayKey?: string;
  };
};

/**
 * What the chat does when its engine reports a tool call or a turn's end, whichever engine runs
 * it: the page acts on a drafted comment and switches to the diff for code it shows, and a turn
 * that completed offers follow-ups.
 */
export function useSidebarChatEvents(id) {
  const $ = _c(23);
  const {
    owner,
    repo,
    number,
    installationId
  } = usePrStore();
  const {
    activeTab,
    switchToChangesTab
  } = usePrTabNavigationStore();
  const {
    togglePrCommentInput,
    activePrCommentInput
  } = usePrCommentInput();
  const navigateToFile = useDiffNavigationStore(_temp);
  const getPrV2Draft = usePrV2DraftStore(_temp2);
  const setPrV2DraftForFile = usePrV2DraftStore(_temp3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const processedToolCallKeys = useRef(t0);
  const [liveFinishedMessageId, setLiveFinishedMessageId] = useState(null);
  let t1;
  if ($[1] !== activePrCommentInput || $[2] !== activeTab || $[3] !== getPrV2Draft || $[4] !== id || $[5] !== navigateToFile || $[6] !== number || $[7] !== owner || $[8] !== repo || $[9] !== setPrV2DraftForFile || $[10] !== switchToChangesTab || $[11] !== togglePrCommentInput) {
    t1 = t2 => {
      const {
        toolCall
      } = t2;
      if (useChatStore.getState().chatId !== id) {
        return;
      }
      if (toolCall.toolName === "addPrComment") {
        const key = toolCall.replayKey ?? toolCall.toolCallId;
        if (processedToolCallKeys.current.has(key)) {
          return;
        }
        const input = toolCall.input;
        if (!hasRequiredAddPrCommentInput(input)) {
          return;
        }
        processedToolCallKeys.current.add(key);
        applyAddPrComment({
          args: input,
          owner,
          repo,
          number,
          navigateToFile,
          getDraft: getPrV2Draft,
          setDraftForFile: setPrV2DraftForFile,
          togglePrCommentInput,
          activePrCommentInput,
          activeTab,
          switchToChangesTab
        });
        return;
      }
      if (toolCall.toolName === "focusCode") {
        dispatchPrV2TabHandoff(PR_V2_TABS.CHANGES);
      }
    };
    $[1] = activePrCommentInput;
    $[2] = activeTab;
    $[3] = getPrV2Draft;
    $[4] = id;
    $[5] = navigateToFile;
    $[6] = number;
    $[7] = owner;
    $[8] = repo;
    $[9] = setPrV2DraftForFile;
    $[10] = switchToChangesTab;
    $[11] = togglePrCommentInput;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const handleToolCall = useEventCallback(t1);
  let t2;
  if ($[13] !== id || $[14] !== installationId || $[15] !== number || $[16] !== owner || $[17] !== repo) {
    t2 = t3 => {
      const {
        lastMessageId,
        completed
      } = t3;
      if (useChatStore.getState().chatId !== id) {
        return;
      }
      if (completed) {
        setLiveFinishedMessageId(lastMessageId);
      }
      trackClientEvent("v2_chat_message_sent", installationId?.toString(), {
        owner,
        repo,
        pr_number: number
      });
    };
    $[13] = id;
    $[14] = installationId;
    $[15] = number;
    $[16] = owner;
    $[17] = repo;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const handleTurnEnd = useEventCallback(t2);
  let t3;
  if ($[19] !== handleToolCall || $[20] !== handleTurnEnd || $[21] !== liveFinishedMessageId) {
    t3 = {
      handleToolCall,
      handleTurnEnd,
      liveFinishedMessageId
    };
    $[19] = handleToolCall;
    $[20] = handleTurnEnd;
    $[21] = liveFinishedMessageId;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
}

/**
 * The page's AI context as a message sends it: the file in view at send time, and file contents
 * the code-changes payload stripped hydrated back.
 */
function _temp3(state_1) {
  return state_1.setDraftForFile;
}
function _temp2(state_0) {
  return state_0.getDraft;
}
function _temp(state) {
  return state.navigateToFile;
}
export function useSidebarChatRequestContext(aiContext) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  const {
    owner,
    repo,
    number
  } = usePrStore();
  const comparisonHeadSha = aiContext.headSha ?? aiContext.pr?.head?.sha ?? null;
  const comparisonBaseSha = aiContext.baseSha ?? aiContext.pr?.base?.sha ?? null;
  let t0;
  if ($[0] !== aiContext || $[1] !== comparisonBaseSha || $[2] !== comparisonHeadSha || $[3] !== number || $[4] !== owner || $[5] !== queryClient || $[6] !== repo) {
    t0 = async () => {
      const requestContext = buildSidebarChatAiContext(aiContext);
      return {
        ...requestContext,
        codeContent: requestContext.codeContent ? await prepareHydratedCodeContentForChatRequest(queryClient, {
          owner,
          repo,
          number,
          headSha: comparisonHeadSha,
          baseSha: comparisonBaseSha
        }, requestContext.codeContent) : requestContext.codeContent
      };
    };
    $[0] = aiContext;
    $[1] = comparisonBaseSha;
    $[2] = comparisonHeadSha;
    $[3] = number;
    $[4] = owner;
    $[5] = queryClient;
    $[6] = repo;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return useEventCallback(t0);
}
type SendArguments = Parameters<SidebarChatEngine['sendMessage']>;
export const HELD_CHAT_MESSAGE_ID = 'held-message';

/**
 * The chat's engine as the person sees it while the page cannot send yet: before it knows which
 * agent answers the chat, while the chat's saved conversation loads, or while the pull request's
 * diff loads. The chat shows at once either way. A message sent meanwhile shows as sent and
 * reaches the engine as soon as it can take it; any further one waits in the chat's own queue,
 * since the held one reads as in flight. An engine the chat already has keeps showing its own
 * messages and state, such as a turn still running: only its sends wait.
 */
export function useHeldUntilReady(engine, ready) {
  const $ = _c(35);
  const [held, setHeld] = useState(null);
  const heldRef = useRef(null);
  const live = ready && engine !== null;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async (...t1) => {
      const args = t1;
      if (heldRef.current !== null) {
        const body = args[1]?.body as {
          model?: unknown;
        } | undefined;
        useChatStore.getState().addPendingPrompt(sentText(args), typeof body?.model === "string" ? body.model : undefined);
        return;
      }
      heldRef.current = args;
      setHeld(args);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const hold = useEventCallback(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      heldRef.current = null;
      setHeld(null);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const drop = useEventCallback(t1);
  let t2;
  if ($[2] !== drop || $[3] !== engine) {
    t2 = async () => {
      await drop();
      await engine?.stop();
    };
    $[2] = drop;
    $[3] = engine;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const dropAndStop = useEventCallback(t2);
  const busy = engine?.status === "submitted" || engine?.status === "streaming";
  let t3;
  let t4;
  if ($[5] !== busy || $[6] !== engine || $[7] !== held || $[8] !== live) {
    t3 = () => {
      if (live && !busy && held !== null) {
        heldRef.current = null;
        setHeld(null);
        engine.sendMessage(...held);
      }
    };
    t4 = [busy, engine, held, live];
    $[5] = busy;
    $[6] = engine;
    $[7] = held;
    $[8] = live;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useLayoutEffect(t3, t4);
  if (live && held === null) {
    return engine;
  }
  if (engine === null) {
    let t5;
    if ($[11] !== held) {
      t5 = held === null ? [] : [heldMessageBubble(held)];
      $[11] = held;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    const t6 = held === null ? "ready" : "submitted";
    let t7;
    if ($[13] !== drop || $[14] !== hold || $[15] !== t5 || $[16] !== t6) {
      t7 = {
        messages: t5,
        setMessages: _temp4,
        sendMessage: hold,
        status: t6,
        stop: drop,
        error: undefined,
        clearError: _temp5
      };
      $[13] = drop;
      $[14] = hold;
      $[15] = t5;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  if (held === null) {
    let t5;
    if ($[18] !== engine || $[19] !== hold) {
      t5 = {
        ...engine,
        sendMessage: hold
      };
      $[18] = engine;
      $[19] = hold;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  const t5 = engine.messages;
  let t6;
  if ($[21] !== held) {
    t6 = heldMessageBubble(held);
    $[21] = held;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== engine.messages || $[24] !== t6) {
    t7 = [...t5, t6];
    $[23] = engine.messages;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== engine || $[27] !== hold || $[28] !== t7) {
    t8 = {
      ...engine,
      messages: t7,
      sendMessage: hold
    };
    $[26] = engine;
    $[27] = hold;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const withHeld = t8;
  let t9;
  if ($[30] !== busy || $[31] !== drop || $[32] !== dropAndStop || $[33] !== withHeld) {
    t9 = busy ? {
      ...withHeld,
      stop: dropAndStop
    } : {
      ...withHeld,
      status: "submitted",
      stop: drop
    };
    $[30] = busy;
    $[31] = drop;
    $[32] = dropAndStop;
    $[33] = withHeld;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}
function _temp5() {}
function _temp4() {}
function sentText([message]: SendArguments): string {
  return message && 'text' in message && typeof message.text === 'string' ? message.text : '';
}
function heldMessageBubble(args: SendArguments): SidebarChatMessage {
  return {
    id: HELD_CHAT_MESSAGE_ID,
    role: 'user',
    parts: [{
      type: 'text',
      text: sentText(args)
    }]
  };
}